import axios from 'axios';
import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { serializeError } from './apiUtils';

const AZURE_CONFIG = {
  key: '9eiqNFW2EGxZN09M6HktdPIt8Ks0UfuidOJYm9IA5VFgEaj8pTVzJQQJ99AKACYeBjFXJ3w3AAAYACOGlq0e',
  region: 'eastus',
  endpoint: 'https://eastus.tts.speech.microsoft.com/'
};

// Increased timeout and added retry logic
const TIMEOUT_MS = 30000; // 30 seconds
const MAX_RETRIES = 2;

const testAzureTts = async () => {
  return new Promise<boolean>((resolve) => {
    try {
      const speechConfig = sdk.SpeechConfig.fromSubscription(AZURE_CONFIG.key, AZURE_CONFIG.region);
      speechConfig.speechSynthesisVoiceName = 'en-US-JennyNeural';
      
      const audioOutputStream = sdk.AudioOutputStream.createPullStream();
      const audioConfig = sdk.AudioConfig.fromStreamOutput(audioOutputStream);
      
      const synthesizer = new sdk.SpeechSynthesizer(speechConfig, audioConfig);
      
      // Set a timeout for the entire operation
      const timeoutId = setTimeout(() => {
        synthesizer.close();
        resolve(false);
        console.warn('Azure TTS test timed out after', TIMEOUT_MS, 'ms');
      }, TIMEOUT_MS);

      synthesizer.speakTextAsync(
        'Test message',
        result => {
          clearTimeout(timeoutId);
          const success = result.audioData && result.audioData.byteLength > 0;
          synthesizer.close();
          resolve(success);
        },
        error => {
          clearTimeout(timeoutId);
          console.error('Azure TTS test failed:', serializeError(error));
          synthesizer.close();
          resolve(false);
        }
      );
    } catch (error) {
      console.error('Azure TTS test failed:', serializeError(error));
      resolve(false);
    }
  });
};

const retryWithDelay = async (fn: () => Promise<any>, retries: number, delay: number): Promise<any> => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithDelay(fn, retries - 1, delay * 2);
    }
    throw error;
  }
};

export const testAzureVoicesList = async () => {
  const fetchVoices = async () => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
      const response = await axios.get(
        `${AZURE_CONFIG.endpoint}cognitiveservices/voices/list`,
        {
          headers: {
            'Ocp-Apim-Subscription-Key': AZURE_CONFIG.key
          },
          signal: controller.signal,
          timeout: TIMEOUT_MS,
          validateStatus: (status) => status === 200
        }
      );

      clearTimeout(timeout);

      const success = response.data && Array.isArray(response.data);
      if (!success) {
        console.warn('Azure voices list test failed: Invalid response format', {
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data
        });
      }
      return success;
    } catch (error: any) {
      clearTimeout(timeout);
      
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
          console.warn('Azure voices list request timed out');
        } else if (error.response) {
          console.warn('Azure voices list request failed:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          });
        } else if (error.request) {
          console.warn('Azure voices list request failed: No response received');
        }
      }
      throw error;
    }
  };

  try {
    return await retryWithDelay(fetchVoices, MAX_RETRIES, 1000);
  } catch (error) {
    console.error('Azure voices list test failed after retries:', serializeError(error));
    return false;
  }
};

export default testAzureTts;